import * as React from 'react';
import { UitkEmailInput, UitkTextArea, UitkTextInput } from 'uitk-react-input';
import { UitkPrimaryButton } from 'uitk-react-button';
import { NewConversationViewModel } from 'src/common/models';
import { UitkLoader } from 'uitk-react-loader';
import { ClickTracker } from 'bernie-analytics';
import { AskAQuestionResult } from 'components/infosite/conversations/ask-a-question-result';
import { ValidationProps, withValidation } from 'components/infosite/conversations/validation';

const TrackedSubmitButton = ClickTracker(UitkPrimaryButton);
const TrackedTextInput = ClickTracker(UitkTextInput);
const TrackedTextArea = ClickTracker(UitkTextArea);
const TrackedEmailInput = ClickTracker(UitkEmailInput);

export interface AskAQuestionData {
  name?: string;
  question: string;
  email: string;
}
export interface AskAQuestionProps extends NewConversationViewModel, ValidationProps {
  loading: boolean;
  success: boolean;
  alert: string;
  action: string;
  onSubmit: (data: AskAQuestionData) => void;
}

interface AskAQuestionState {
  name?: string;
  question: string;
  email: string;
  errors: object;
}
class QuestionForm extends React.Component<AskAQuestionProps, AskAQuestionState> {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      question: '',
      email: '',
      errors: {}
    };

    this.handleChangeEmail = this.handleChangeEmail.bind(this);
    this.handleChangeName = this.handleChangeName.bind(this);
    this.handleChangeQuestion = this.handleChangeQuestion.bind(this);
    this.handleSubmitOverride = this.handleSubmitOverride.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
  }

  public render() {
    const { form, loading, title, success, alert, action } = this.props;
    const commentField = form && form.comment;
    const nameField = form && form.name;
    const emailAddressField = form && form.emailAddress;
    const resultText = form && form.resultText;
    const actionText = form && form.actionText;

    const { name, email, question, errors } = this.state;

    const commentFieldId = 'conversation-comment';
    const askAQuestionResultProps = {
      success,
      message: alert,
      action,
      linkClickFocusId: commentFieldId
    };

    const showForm = !(alert && success);

    return (
      <React.Fragment>
        {alert && <AskAQuestionResult {...askAQuestionResultProps} />}
        {showForm && (
          <form className="uitk-grid all-y-gutter-three" noValidate={true}>
            {title && <h3 className="uitk-cell uitk-type-heading-400 all-y-gutter-six">{title}</h3>}
            {commentField && (
              <div className="uitk-cell">
                <TrackedTextArea
                  id={commentFieldId}
                  label={commentField.label}
                  placeholder={commentField.placeholder}
                  onChange={this.handleChangeQuestion}
                  value={question}
                  disabled={loading}
                  rows={5}
                  rfrr="AskQuestion.QuestionText"
                  linkName="Question text"
                  invalid={errors['question'] && commentField.invalid}
                />
              </div>
            )}
            {nameField && (
              <div className="uitk-cell">
                <TrackedTextInput
                  id="conversation-name"
                  disabled={loading}
                  label={nameField.label}
                  value={name}
                  onChange={this.handleChangeName}
                  rfrr="AskQuestion.Name"
                  linkName="Name input"
                  invalid={errors['name'] && nameField.invalid}
                />
              </div>
            )}
            {emailAddressField && (
              <div className="uitk-cell">
                <TrackedEmailInput
                  id="conversation-email"
                  disabled={loading}
                  label={emailAddressField.label}
                  value={email}
                  onChange={this.handleChangeEmail}
                  rfrr="AskQuestion.Email"
                  linkName="Email input"
                  invalid={errors['email'] && emailAddressField.invalid}
                />
              </div>
            )}
            {(actionText || loading) && (
              <div className="uitk-cell all-y-gutter-six">
                <SubmitButton actionText={actionText} loading={loading} onSubmit={this.handleSubmitOverride} />
              </div>
            )}
            {resultText && (
              <div className="uitk-cell all-y-gutter-two">
                <p className="uitk-type-200 uitk-type-center">{resultText}</p>
              </div>
            )}
          </form>
        )}
      </React.Fragment>
    );
  }

  public handleSubmitOverride() {
    const { name, email, question } = this.state;
    if (this.isFormValid()) {
      const data = {
        name,
        question,
        email
      };
      this.props.onSubmit(data);
    }
  }

  public handleChangeName(event) {
    this.setState({
      name: event.target.value
    });
  }

  public handleChangeQuestion(event) {
    this.setState({
      question: event.target.value
    });
  }

  public handleChangeEmail(event) {
    this.setState({
      email: event.target.value
    });
  }

  public isFormValid() {
    const { email, question, name } = this.state;
    const { form, validateEmail, validateName, validateQuestion } = this.props;
    const isQuestionValid = validateQuestion(question);
    const isEmailValid = validateEmail(email);

    const nameRequired = form && form.name && form.name.required;
    const isNameValid = !nameRequired || validateName(name);
    this.setState({
      errors: {
        email: !isEmailValid,
        question: !isQuestionValid,
        name: !isNameValid
      }
    });

    return isEmailValid && isQuestionValid && isNameValid;
  }
}

const SubmitButton = ({ actionText, loading, onSubmit }) => {
  if (loading) {
    return (
      <div className="uitk-type-center">
        <UitkLoader isSmall={true} />
      </div>
    );
  }

  return (
    <TrackedSubmitButton
      isFullWidth={true}
      text={actionText}
      onClick={onSubmit}
      id="question-submit-btn"
      rfrr="AskQuestion.Submit"
      linkName="Submit question"
    />
  );
};

export const AskAQuestion = withValidation(QuestionForm);
